import { useEffect, useMemo, useState } from "react";
import { uuid } from "@/utils/string.ts";
import { useTranslation } from "react-i18next";
import { downloadLocalFile } from "@/utils/download";
import { useMe, useUserEventsAndMaterials } from "@/data/user";
import {
  Select,
  SelectItem,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
} from "@/components/ui/Select.tsx";
// import pdf from "@/assets/files/IELTSFAQ2.pdf";
import List from "@edusogno/composites/list";
import pdfV2 from "@/assets/files/Guide - Frequently asked questions ENG.pdf";
import NoItem from "@/components/NoItem.tsx";
import Button from "@edusogno/primitives/button";
import PDFFileIcon from "@/components/icons/PDFFileIcon";
import MaterialItem from "@/components/materials/MaterialItem.tsx";
import ContentLoader from "@edusogno/composites/content-loader";
import MaterialsBadge from "@/components/materials/MaterialsBadge";
import MaterialDownloadIcon from "@/components/icons/MaterialDownloadIcon";

interface SubtypePriorityMap {
  [level: string]: string;
}

const subtypePriorityMap: SubtypePriorityMap = {
  C1: "Advanced",
  "B2+": "Advanced",
  B2: "Advanced",
  "B2-": "Intermediate",
  "B1+": "Intermediate",
  B1: "Basic",
  A2: "Basic",
  A1: "Beginner",
  "A0+": "Starter",
  "A0-": "Zero",
};

const selectAppropriateSubtype = (
  categories: string[],
  englishLevel: string,
): string => {
  const preferredSubtype: string =
    subtypePriorityMap[englishLevel] || "Beginner";

  return categories?.includes(preferredSubtype)
    ? preferredSubtype
    : categories[0] || "Beginner";
};

export function MaterialsList() {
  const { t } = useTranslation();
  const { me } = useMe();
  const livelloInglese = me?.item?.user?.english_level;
  const { eventMaterials } = useUserEventsAndMaterials();
  const materialsData = eventMaterials?.item?.materials || [];

  const [filter, setFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isIeltsCourse, setIsIeltsCourse] = useState(false);

  const lessons = Array.from(
    new Set(materialsData.map((material) => material?.lesson)),
  ).filter((e) => e);

  const filterOptions = [
    { label: t("exercises.all"), value: "all" },
    ...lessons
      .map((lesson) => {
        switch (lesson) {
          case "IELTS":
            return [
              { label: "IELTS Reading", value: "ielts_reading" },
              { label: "IELTS Writing", value: "ielts_writing" },
              { label: "IELTS Listening", value: "ielts_listening" },
              { label: "IELTS Speaking", value: "ielts_speaking" },
            ];
          case "Linguaskill":
            return [
              { label: "All Linguaskill", value: "linguaskill" },
              { label: "Linguaskill Reading", value: "linguaskill_reading" },
              { label: "Linguaskill Writing", value: "linguaskill_writing" },
              {
                label: "Linguaskill Listening",
                value: "linguaskill_listening",
              },
              { label: "Linguaskill Speaking", value: "linguaskill_speaking" },
            ];
          case "Speaking":
            return { label: "Speaking", value: "speaking" };
          case "Grammar":
            return { label: "Grammar", value: "grammar" };
          default:
            return { label: lesson, value: lesson?.toLowerCase() };
        }
      })
      .flat(),
  ];

  const modMaterials = materialsData.map((material) => ({
    ...material,
    subtype: selectAppropriateSubtype(
      material.category || [],
      livelloInglese || "A1",
    ),
  }));

  const materials = useMemo(
    () =>
      modMaterials.filter((material) => {
        const lesson = material.lesson || "";
        const category = material.category?.[0] || "";

        return (
          filter === "all" ||
          (filter.startsWith("ielts_")
            ? lesson === "IELTS" &&
            category.toLowerCase() === filter.split("_")[1]
            : filter.startsWith("linguaskill_")
              ? lesson === "Linguaskill" &&
              category.toLowerCase() === filter.split("_")[1]
              : filter.toLowerCase() === lesson.toLowerCase())
        );
      }),
    [modMaterials, filter],
  );

  useEffect(() => {
    const isIeltsCourseCheck = () => {
      const courseStructure =
        me?.item?.user?.products?.[0]?.course_info?.course_structure;

      return courseStructure?.some((structure) => structure.ielts > 0) ?? false;
    };

    setIsIeltsCourse(isIeltsCourseCheck());
  }, [me]);

  useEffect(() => {
    if (eventMaterials && me) {
      setIsLoading(false);
    }
  }, [eventMaterials, me]);

  return (
    <div className="lg:max-h-[calc(100%_-_6%)] max-h-[calc(100%_-_6%)] h-full">
      <div className="flex flex-row items-center justify-end gap-4 px-0">
        <Select defaultValue="all" onValueChange={setFilter}>
          <SelectTrigger
            showIcon={false}
            className="flex flex-none justify-between outline-none px-2 text-sm md:flex-none w-[112px] md:w-[180px] h-[40px] md:h-[40px] items-center bg-light border-0 text-brand"
          >
            <SelectValue placeholder="Filter value" className="text-sm" />
          </SelectTrigger>

          <SelectContent>
            <SelectGroup>
              {filterOptions.map((option, i) => (
                <SelectItem
                  key={option.value + i}
                  value={option.value}
                  className="text-sm"
                  data-track-eventtype={`Exercise_Filtered_${option.label}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="h-[calc(100%_-_6%)] mt-4 overflow-y-hidden">
        {isLoading ? (
          <ContentLoader />
        ) : materials?.length === 0 ? (
          <NoItem text={t("materials.empty")} />
        ) : (
          <div className="h-full lg:max-h-[calc(100%_-_72px)] max-h-[calc(100%_-_86px)]">
            <List className="w-full overflow-y-auto pr-0 !max-h-full">
              {isIeltsCourse && (
                <div className="flex flex-row items-center justify-between w-full px-6 py-4 rounded-md lg:gap-5 center bg-brand-light">
                  <div className="flex-row flex items-center gap-5 w-[95%]">
                    <div className="relative">
                      <PDFFileIcon className="w-8 min-w-[32px] h-auto" />
                    </div>

                    <div className="until-lg:hidden">
                      <MaterialsBadge type="IELTS" subtype="General" />
                    </div>

                    <div className="flex flex-col">
                      <p className="text-[--secondary] font-semibold text-base line-clamp-1">
                        {t("materials.faq")}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    onClick={() => downloadLocalFile(pdfV2, "IELTS FAQ.pdf")}
                    className="ml-auto w-fit lg:hidden"
                  >
                    <MaterialDownloadIcon className="w-6 h-auto stroke-brand-secondary" />
                  </Button>

                  <Button
                    size="sm"
                    color="primary"
                    variant="contained"
                    onClick={() => downloadLocalFile(pdfV2, "IELTS FAQ.pdf")}
                    className="hidden ml-auto lg:block"
                  >
                    {t("btns.view")}
                  </Button>
                </div>
              )}

              {materials?.map((material) => (
                <MaterialItem
                  id={material.id}
                  tk={material.token}
                  key={uuid()}
                  name={material.name}
                  type={material.type}
                  isNew={material.isNew}
                  subtype={material.subtype}
                  fileType={material.type}
                  lessonOf={material.lessonOf}
                  resource={material.resource}
                  isPrecourse={material.precourse}
                  materialType={material.lesson}
                  images={material?.images && material?.images?.length > 0 ? material.images : null}
                />
              ))}
            </List>
          </div>
        )}
      </div>
    </div>
  );
}

export default MaterialsList;
